import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { 
  FiSearch, FiExternalLink, FiMail, 
  FiPhone, FiRefreshCw, FiPlus, FiX,
  FiDollarSign, FiCalendar, FiClock
} from 'react-icons/fi'
import { toast } from 'react-hot-toast'
import './Clients.css'

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:3001/api'

const statusColors = {
  'active': 'status-success',
  'lead': 'status-warning',
  'completed': 'status-info',
  'paused': 'status-danger'
}

const serviceIcons = {
  automation: '⚡',
  chatbot: '💬',
  rag: '🔍',
  coding: '💻',
  consulting: '🧠'
}

const serviceNames = {
  automation: 'AI Automation',
  chatbot: 'AI Chatbot',
  rag: 'Knowledge Base',
  coding: 'AI Coding',
  consulting: 'Strategy'
}

const Clients = () => {
  const [statusFilter, setStatusFilter] = useState('')
  const [serviceFilter, setServiceFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editClient, setEditClient] = useState(null)

  const token = localStorage.getItem('token')

  const { data, isLoading, error, refetch } = useQuery(
    ['clients', statusFilter, serviceFilter],
    async () => {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (serviceFilter) params.append('service', serviceFilter)
      const response = await fetch(`${API_BASE}/clients?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) throw new Error('Failed to fetch clients')
      return response.json()
    }
  )

  const { data: stats } = useQuery('client-stats', async () => {
    const response = await fetch(`${API_BASE}/clients/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    if (!response.ok) throw new Error('Failed to fetch stats')
    return response.json()
  })

  const filteredClients = data?.clients?.filter(c => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return c.name?.toLowerCase().includes(q) ||
             c.company?.toLowerCase().includes(q) ||
             c.email?.toLowerCase().includes(q) ||
             c.notes?.toLowerCase().includes(q)
    }
    return true
  }) || []

  const handleSave = async (e) => {
    e.preventDefault()
    const form = e.target
    const body = {
      name: form.name.value,
      company: form.company.value || null,
      email: form.email.value || null,
      phone: form.phone.value || null,
      service: form.service.value,
      status: form.status.value,
      price: parseInt(form.price.value) || 0,
      retainer: parseInt(form.retainer.value) || 0,
      notes: form.notes.value || null
    }

    try {
      const url = editClient 
        ? `${API_BASE}/clients/${editClient.id}`
        : `${API_BASE}/clients`
      const method = editClient ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      })
      if (!response.ok) throw new Error('Failed to save')
      toast.success(editClient ? 'Client updated' : 'Client added!')
      setShowAddModal(false)
      setEditClient(null)
      refetch()
    } catch (err) {
      toast.error('Failed to save client')
    }
  }

  const updateStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/clients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      })
      if (!response.ok) throw new Error('Update failed')
      toast.success(`Status updated`)
      refetch()
    } catch (err) {
      toast.error('Failed to update status')
    }
  }

  const deleteClient = async (id) => {
    if (!confirm('Delete this client? This cannot be undone.')) return
    try {
      const response = await fetch(`${API_BASE}/clients/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) throw new Error('Delete failed')
      toast.success('Client deleted')
      refetch()
    } catch (err) {
      toast.error('Failed to delete')
    }
  }

  const openEdit = (client) => {
    setEditClient(client)
    setShowAddModal(true)
  }

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
  }

  return (
    <div className="clients-page">
      <div className="page-header">
        <div>
          <h1>Clients</h1>
          <p className="page-subtitle">
            Manage your AI service clients, projects, and recurring revenue.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={() => { setEditClient(null); setShowAddModal(true); }}>
            <FiPlus /> Add Client
          </button>
          <button className="btn-secondary" onClick={() => refetch()}>
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="client-stats-row">
        <div className="client-stat-card">
          <div className="client-stat-icon" style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>
            {stats?.total || data?.clients?.length || 0}
          </div>
          <div>
            <div className="client-stat-value">{stats?.total || data?.clients?.length || 0}</div>
            <div className="client-stat-label">Total Clients</div>
          </div>
        </div>
        <div className="client-stat-card">
          <div className="client-stat-icon" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
            {stats?.active || 0}
          </div>
          <div>
            <div className="client-stat-value">{stats?.active || 0}</div>
            <div className="client-stat-label">Active Now</div>
          </div>
        </div>
        <div className="client-stat-card">
          <div className="client-stat-icon" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
            {stats?.leads || 0}
          </div>
          <div>
            <div className="client-stat-value">{stats?.leads || 0}</div>
            <div className="client-stat-label">Leads</div>
          </div>
        </div>
        <div className="client-stat-card">
          <div className="client-stat-icon" style={{ background: 'rgba(6,182,212,0.15)', color: '#06b6d4' }}>
            {stats?.mrr ? formatCurrency(stats.mrr) : '$0'}
          </div>
          <div>
            <div className="client-stat-value">{stats?.mrr ? formatCurrency(stats.mrr) : '$0'}</div>
            <div className="client-stat-label">Monthly Recurring</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="client-filters">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search clients, companies, emails..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <FiX />
            </button>
          )}
        </div>

        <div className="filter-row">
          <div className="status-filters">
            {['', 'active', 'lead', 'completed', 'paused'].map(s => (
              <button
                key={s}
                className={`filter-btn ${statusFilter === s ? 'active' : ''}`}
                onClick={() => setStatusFilter(s === statusFilter ? '' : s)}
              >
                {s ? s : 'All Status'}
              </button>
            ))}
          </div>
          <div className="status-filters">
            {['', 'automation', 'chatbot', 'rag', 'coding', 'consulting'].map(s => (
              <button
                key={s}
                className={`filter-btn ${serviceFilter === s ? 'active' : ''}`}
                onClick={() => setServiceFilter(s === serviceFilter ? '' : s)}
              >
                {s ? serviceNames[s] || s : 'All Services'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Clients grid */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading clients...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <h3>Failed to load clients</h3>
          <p>Check backend connection</p>
          <button className="btn-secondary" onClick={() => refetch()}>Retry</button>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="empty-state">
          <FiDollarSign size={48} />
          <h3>No clients found</h3>
          <p>{searchQuery ? 'Try different search terms' : 'Add your first client to get started'}</p>
        </div>
      ) : (
        <div className="clients-grid">
          {filteredClients.map(client => (
            <div key={client.id} className="client-card">
              <div className="client-card-header">
                <div>
                  <h3 className="client-title">{client.name}</h3>
                  {client.company && (
                    <div className="client-company">{client.company}</div>
                  )}
                </div>
                <div className="client-card-actions">
                  <button className="btn-icon" onClick={() => deleteClient(client.id)} title="Delete client">🗑</button>
                </div>
              </div>

              <div className="client-meta-row">
                <span className={`opp-status ${statusColors[client.status] || 'status-accent'}`}>
                  {client.status}
                </span>
                <span className="client-service">
                  {serviceIcons[client.service] || '🤖'} {serviceNames[client.service] || client.service}
                </span>
              </div>

              {/* Contact info */}
              <div className="client-details">
                {client.email && (
                  <div className="client-detail">
                    <FiMail className="detail-icon" />
                    <span className="client-link">{client.email}</span>
                  </div>
                )}
                {client.phone && (
                  <div className="client-detail">
                    <FiPhone className="detail-icon" />
                    <span>{client.phone}</span>
                  </div>
                )}
              </div>

              {/* Notes preview */}
              {client.notes && (
                <div className="client-notes">
                  <FiCalendar size={12} />
                  <span>{client.notes.length > 80 ? client.notes.substring(0, 80) + '...' : client.notes}</span>
                </div>
              )}

              {/* Pricing */}
              <div className="client-pricing">
                <div className="client-price">
                  <FiDollarSign size={14} />
                  {formatCurrency(client.price)}
                </div>
                {client.retainer > 0 && (
                  <div className="client-retainer">
                    <FiClock size={13} />
                    {formatCurrency(client.retainer)}/mo
                  </div>
                )}
              </div>

              {/* Actions footer */}
              <div className="client-card-footer">
                <select
                  className="status-select"
                  value={client.status}
                  onChange={(e) => updateStatus(client.id, e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="lead">Lead</option>
                  <option value="completed">Completed</option>
                  <option value="paused">Paused</option>
                </select>
                <button className="btn-secondary btn-sm" onClick={() => openEdit(client)}>
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); setEditClient(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editClient ? 'Edit Client' : 'Add New Client'}</h2>
              <button className="modal-close" onClick={() => { setShowAddModal(false); setEditClient(null); }}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Client Name *</label>
                  <input name="name" required defaultValue={editClient?.name || ''} placeholder="e.g. John Smith" />
                </div>
                <div className="form-group">
                  <label>Company / Business</label>
                  <input name="company" defaultValue={editClient?.company || ''} placeholder="e.g. ACME Corp" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email</label>
                    <input name="email" type="email" defaultValue={editClient?.email || ''} placeholder="john@acme.com" />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input name="phone" defaultValue={editClient?.phone || ''} placeholder="+1 555..." />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Service Type</label>
                    <select name="service" defaultValue={editClient?.service || 'automation'}>
                      <option value="automation">⚡ AI Automation (n8n/Make)</option>
                      <option value="chatbot">💬 AI Chatbot</option>
                      <option value="rag">🔍 RAG / Knowledge Base</option>
                      <option value="coding">💻 AI-Assisted Coding</option>
                      <option value="consulting">🧠 AI Strategy Consulting</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" defaultValue={editClient?.status || 'active'}>
                      <option value="active">🟢 Active</option>
                      <option value="lead">🟡 Lead</option>
                      <option value="completed">✅ Completed</option>
                      <option value="paused">⏸️ Paused</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Project Price ($)</label>
                    <input name="price" type="number" defaultValue={editClient?.price || ''} placeholder="3500" min="0" step="100" />
                  </div>
                  <div className="form-group">
                    <label>Monthly Retainer ($)</label>
                    <input name="retainer" type="number" defaultValue={editClient?.retainer || ''} placeholder="300" min="0" step="50" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Notes / What we built</label>
                  <textarea name="notes" rows="3" defaultValue={editClient?.notes || ''} placeholder="e.g. Lead qualification automation — n8n workflow + AI chatbot"></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => { setShowAddModal(false); setEditClient(null); }}>Cancel</button>
                <button type="submit" className="btn-primary">
                  {editClient ? '💾 Update Client' : '💾 Add Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Clients
